import { useMemo, useState } from 'react';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { FormField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Paginator, type PageEvent } from '@/kit/Paginator';
import { Select, SelectOption } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import type { ChartTemplateAccountRow } from '@/core/models/firm-settings.models';
import type { ChartTemplateGrid as Grid } from './ChartTemplateGridBase';
import { LegacyCodeMappingPanel } from './LegacyCodeMappingPanel';
import './ChartTemplateGrid.scss';

const PAGE_SIZE_OPTIONS = [15, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 15;

function SearchField({ value, onSearch }: { value: string; onSearch: (text: string) => void }) {
  const [focused, setFocused] = useState(false);
  return (
    <FormField className="search-field" focused={focused}>
      <Icon name="search" />
      <input
        className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
        placeholder="Search"
        autoComplete="off"
        maxLength={100}
        value={value}
        onChange={(event) => onSearch(event.currentTarget.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
    </FormField>
  );
}

/**
 * The template of the AccountsLimited / SoleTrader / Partnership / Llp components: byte-identical apart from the
 * table's `aria-describedby` (an id that does not exist, so it never had an effect), which is why one component
 * serves the four tabs.
 */
export function ChartTemplateGridView({ grid }: { grid: Grid }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // The first page again whenever the rows change; set while rendering so the page never shows a stale index.
  const [pagedSource, setPagedSource] = useState(grid.dataSource);
  if (pagedSource !== grid.dataSource) {
    setPagedSource(grid.dataSource);
    setPageIndex(0);
  }

  const pageRows = useMemo(
    () => grid.dataSource.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [grid.dataSource, pageIndex, pageSize],
  );

  const columns: DataTableColumn<ChartTemplateAccountRow>[] = [
    {
      id: 'name',
      header: (
        <>
          <Checkbox checked={grid.isAllSelected()} onChange={(event) => grid.toggleAll(event.target.checked)} />
          Account Name
        </>
      ),
      cell: (row) => (
        <>
          <Checkbox
            checked={grid.selectedIds.has(row.id)}
            onChange={(event) => grid.toggleRow(row, event.target.checked)}
          />{' '}
          {row.accountName}
        </>
      ),
    },
    { id: 'category', header: 'Category', cell: (row) => row.category },
    {
      id: 'code',
      header: 'Code',
      cell: (row) =>
        row.isStandard ? (
          <span className="chart-code-standard" title="Standard code">
            {row.code}
          </span>
        ) : (
          row.code
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <span className={row.isActive && !row.isArchived ? 'text-green' : 'text-red'}>{row.status}</span>,
    },
    {
      id: 'archive',
      header: 'Archive',
      cell: (row) => (
        <Checkbox checked={grid.archivedShown(row)} onChange={(event) => grid.onArchive(row, event.target.checked)} />
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (row) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Edit" onClick={() => grid.onEditClick(row)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Delete" onClick={() => grid.openDeleteClick(row)}>
            <Icon name="delete_outline" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="chart-template-grid">
      <div className="content-header mb-0">
        <div className="content-header-left mb-1">
          <Button className="action-button primary-button" onClick={grid.onAddClick}>
            <Icon name="add" />
            <span>Add Account</span>
          </Button>
          <SearchField value={grid.searchText} onSearch={grid.onSearch} />
        </div>

        <div className="content-header-right mb-1">
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            clearable={false}
            value={grid.status}
            onChange={(value) => grid.onStatusChange(value as number | null)}
          >
            {grid.statuses.map((option) => (
              <SelectOption key={option.id} value={option.id}>
                {option.name}
              </SelectOption>
            ))}
          </Select>
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            clearable={false}
            value={grid.accountTypeFilter}
            onChange={(value) => grid.onAccountTypeChange(value as number | null)}
          >
            <SelectOption value={0}>All Categories</SelectOption>
            {grid.accountTypes.map((type) => (
              <SelectOption key={type.id} value={type.id}>
                {type.name}
              </SelectOption>
            ))}
          </Select>
          <div className="toolbar">
            <Button title="Export to Excel" onClick={() => grid.onExport(2)}>
              <Icon name="border_all" className="icon-color-xls" />
            </Button>
            <Button title="Export to CSV" onClick={() => grid.onExport(1)}>
              <Icon name="description" outlined className="icon-color-xls" />
            </Button>
            <Button title="Export to PDF" onClick={() => grid.onExport(3)}>
              <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
            </Button>
          </div>
        </div>
      </div>
      <SelectionBar
        count={grid.selectedIds.size}
        onDelete={grid.deleteSelected}
        onExport={() => grid.onExport(2)}
        onClear={() => grid.toggleAll(false)}
      />
      {/* BUG-067: with the paginator below, the grid takes the shorter paged height (Angular's mat-table-wrapper-pagination);
          at the full height the paginator sat below the fold and rows 16+ (e.g. 3 of the 18 Disallowable accounts) could not be reached. */}
      <div
        className={`mat-table-wrapper${grid.dataSource.length > 0 ? ' mat-table-wrapper-pagination' : ''}${grid.legacyMatches.length > 0 ? ' has-legacy-mapping' : ''}`}
      >
        <DataTable stickyHeader columns={columns} rows={pageRows} rowKey={(row) => row.id} />
      </div>
      {grid.dataSource.length > 0 && (
        <Paginator
          length={grid.dataSource.length}
          pageSize={pageSize}
          pageIndex={pageIndex}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          showFirstLastButtons
          onPage={(event: PageEvent) => {
            setPageIndex(event.pageIndex);
            setPageSize(event.pageSize);
          }}
        />
      )}
      <LegacyCodeMappingPanel rows={grid.legacyMatches} onShowCurrent={grid.applySearch} />
    </div>
  );
}
